/**
 * Form component for recruitment request informations with cascading field dependencies.
 * Implements ControlValueAccessor for seamless integration with reactive forms.
 */
import { 
  Component, 
  Input,
  Output,
  EventEmitter,
  OnInit,
  forwardRef, 
  inject,
  computed,
  signal,
  effect,
  ChangeDetectionStrategy
} from '@angular/core';

import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import { 
  RecruitmentDirection as Direction,
  RecruitmentServiceFonctionnel as ServiceFonctionnel 
} from '../../../core/models/domains/recruitment';
import { InformationsFormValue, DemandeRecrutementInformationsValue } from '../../../core/models/api/requests';
import { Poste } from '../../../core/models/domains/posts';
import { TypeRecrutement } from '../../../core/models/domains/recruitment';
import { Pole } from '../../../core/models/shared';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { InformationsFormDataService } from './informations-form-state.service';
import { AutocompleteFieldComponent } from '../autocomplete-field/autocomplete-field.component';
import { distinctUntilChanged } from 'rxjs/operators';
import { RecruitmentUtils } from '../../utils/recruitment.utils';

@Component({
    selector: 'app-informations-form',
    imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatButtonModule,
    AutocompleteFieldComponent
],
    templateUrl: './informations-form.component.html',
    styleUrls: ['./informations-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InformationsFormComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => InformationsFormComponent),
            multi: true
        }
    ]
})
export class InformationsFormComponent implements ControlValueAccessor, Validator, OnInit {
  // Make Validators available in template
  public ValidatorsRef = Validators;
  @Input() readOnly = false;
  @Input() demandeService: ServiceFonctionnel | null | undefined;
  @Output() posteSelected = new EventEmitter<Poste | null>();

  private readonly fb = inject(FormBuilder);
  readonly dataService = inject(InformationsFormDataService);
  Validators = Validators; 
  readonly form: FormGroup;
  private readonly _readOnly = signal(false);
  private readonly _demandeService = signal<ServiceFonctionnel | null>(null);
  private readonly _externalValue = signal<InformationsFormValue | null>(null);
  
  private initialDataLoaded = false;
  private pendingPrefill: InformationsFormValue | null = null;
  private readonly _selectedPole = signal<Pole | null>(null);
  private readonly _selectedDirection = signal<Direction | null>(null);
  private readonly _selectedService = signal<ServiceFonctionnel | null>(null);
  private readonly _selectedIntitulePoste = signal<Poste | null>(null);
  private readonly _selectedNumPoste = signal<Poste | null>(null);
  private readonly _selectedTypeRecrutement = signal<TypeRecrutement | null>(null);

  readonly isVacataireSelected = computed(() => RecruitmentUtils.isVacataireType(this._selectedTypeRecrutement()));
  
  private onChange: (value: InformationsFormValue) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidationChange: () => void = () => {};

  readonly canSelectDirection = computed(() => this._selectedPole() !== null);
  readonly canSelectService = computed(() => this._selectedDirection() !== null);
  readonly canSelectPoste = computed(() => this._selectedDirection() !== null);
  
  readonly autoSelectedDirection = computed(() => {
    const directions = this.dataService.directionList();
    return directions.length === 1 ? directions[0] : null;
  });
  
  readonly shouldAutoSetDemandeService = computed(() => {
    const demande = this._demandeService();
    const services = this.dataService.serviceFonctionnelList();
    return demande && services.some(s => s.id === demande.id);
  });
  
  readonly isDirectionDisabled = computed(() => 
    this._readOnly() || !this.canSelectDirection() || this.autoSelectedDirection() !== null
  );
  
  readonly isServiceDisabled = computed(() => 
    this._readOnly() || !this.canSelectService()
  );
  
  readonly isPosteDisabled = computed(() => 
    this._readOnly() || !this.canSelectPoste() || this.isVacataireSelected()
  );

  constructor() {
    this.form = this.fb.group({
      typeRecrutement: [null, Validators.required],
      pole: [{ value: null, disabled: true }, Validators.required],
      direction: [null, Validators.required],
      service: [null],
      numPoste: [null, Validators.required],
      intitulePoste: [null, Validators.required],
      motifLiberation: [null, Validators.required]
    });

    this.setupFormTracking();
    this.setupFormFieldStateEffects();
    this.setupValidationEffects();
  }

  async ngOnInit(): Promise<void> {
    this._readOnly.set(this.readOnly);
    this._demandeService.set(this.demandeService || null);
    await this.loadInitialData();
  }

  private async loadInitialData(): Promise<void> {
    try {
      await this.dataService.loadStaticData();
      const pendingValue = this.pendingPrefill ?? this._externalValue();
      const userPole = await this.dataService.getCurrentUserPole();
      const poleToApply = pendingValue?.pole ?? userPole ?? null;

      if (poleToApply) {
        this._selectedPole.set(poleToApply);
        this.form.patchValue({ pole: poleToApply }, { emitEvent: false });

        if (poleToApply.id) {
          await this.loadDirectionsForPole(poleToApply.id);
        }
      }

      if (pendingValue?.direction) {
        this._selectedDirection.set(pendingValue.direction);
        this.form.patchValue({ direction: pendingValue.direction }, { emitEvent: false });
      }

      if (pendingValue?.direction?.id) {
        await this.loadServicesForDirection(pendingValue.direction.id);
      }

      if (pendingValue?.service) {
        this._selectedService.set(pendingValue.service);
        this.form.patchValue({ service: pendingValue.service }, { emitEvent: false });
      }

      if (pendingValue?.direction?.id) {
        await this.loadPostesForDirectionAndService(pendingValue.direction.id, pendingValue.service?.id);
      }

      if (pendingValue?.intitulePoste) {
        this._selectedIntitulePoste.set(pendingValue.intitulePoste as Poste | null);
        this.form.patchValue({ intitulePoste: pendingValue.intitulePoste }, { emitEvent: false });
      }

      if (pendingValue?.numPoste) {
        this._selectedNumPoste.set(pendingValue.numPoste as Poste | null);
        this.form.patchValue({ numPoste: pendingValue.numPoste }, { emitEvent: false });
      }
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      this.initialDataLoaded = true;
      if (this.pendingPrefill) {
        void this.ensureCascadingDataForPrefilledValue(this.pendingPrefill);
      }
    }
  }

  private setupFormFieldStateEffects(): void {
    effect(() => this.updateFieldState('direction', this.isDirectionDisabled(), [Validators.required]));
    effect(() => this.updateFieldState('service', this.isServiceDisabled()));
    effect(() => this.updatePosteFieldsState());
  }

  private updateFieldState(fieldName: string, shouldDisable: boolean, validators: any[] = []): void {
    const control = this.form.get(fieldName);
    if (!control) return;

    if (shouldDisable && !control.disabled) {
      control.disable({ emitEvent: false });
    } else if (!shouldDisable && control.disabled) {
      control.enable({ emitEvent: false });
      control.setValidators(validators);
    }
    
    control.updateValueAndValidity();
  }

  private updatePosteFieldsState(): void {
    const intituleControl = this.form.get('intitulePoste');
    const numControl = this.form.get('numPoste');
    
    if (!intituleControl || !numControl) return;

    const shouldDisable = this.isPosteDisabled();

    if (shouldDisable) {
      if (!intituleControl.disabled) {
        intituleControl.clearValidators();
        intituleControl.disable({ emitEvent: false });
      }
      if (!numControl.disabled) {
        numControl.clearValidators();
        numControl.disable({ emitEvent: false });
      }
    } else {
      if (intituleControl.disabled) {
        intituleControl.enable({ emitEvent: false });
      }
      if (numControl.disabled) {
        numControl.enable({ emitEvent: false });
      }
    }
    
    intituleControl.updateValueAndValidity();
    numControl.updateValueAndValidity();
  }

  private setupValidationEffects(): void {
    effect(() => {
      const typeRecrutement = this._selectedTypeRecrutement();
      const isVacataire = RecruitmentUtils.isVacataireType(typeRecrutement);
      this.updatePosteValidators(!!isVacataire);
    }, { allowSignalWrites: true });
  }

  private updatePosteValidators(isVacataire: boolean): void {
    const intituleControl = this.form.get('intitulePoste');
    const numControl = this.form.get('numPoste');

    if (!intituleControl || !numControl) return;

    intituleControl.clearValidators();
    numControl.clearValidators();

    if (isVacataire) {
      if (intituleControl.value || numControl.value || this._selectedIntitulePoste() || this._selectedNumPoste()) {
        this.clearPosteSelection();
      }

      intituleControl.setErrors(null);
      numControl.setErrors(null);

      if (!intituleControl.disabled) {
        intituleControl.disable({ emitEvent: false });
      }
      if (!numControl.disabled) {
        numControl.disable({ emitEvent: false });
      }
    } else {
      intituleControl.setValidators([Validators.required]);
      numControl.setValidators([Validators.required]);

      if (intituleControl.disabled && !this.isPosteDisabled()) {
        intituleControl.enable({ emitEvent: false });
      }
      if (numControl.disabled && !this.isPosteDisabled()) {
        numControl.enable({ emitEvent: false });
      }
    }

    intituleControl.updateValueAndValidity({ emitEvent: false });
    numControl.updateValueAndValidity({ emitEvent: false });
    this.form.updateValueAndValidity({ emitEvent: false });
    this.onValidationChange();
  }
  private setupFormTracking(): void {
    this.form.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
      this.onChange(this.form.getRawValue());
    });

    this.form.statusChanges.pipe(distinctUntilChanged()).subscribe(() => {
      this.onTouched();
      this.onValidationChange();
    });

    this.form.get('pole')?.valueChanges.subscribe(pole => {
      if (pole !== this._selectedPole()) {
        this.onPoleChange(pole);
      }
    });

    this.form.get('direction')?.valueChanges.subscribe(direction => {
      if (direction !== this._selectedDirection()) {
        this.onDirectionChange(direction);
      }
    });

    this.form.get('service')?.valueChanges.subscribe(service => {
      if (service !== this._selectedService()) {
        this.onServiceChange(service);
      }
    });

    this.form.get('intitulePoste')?.valueChanges.subscribe(poste => {
      if (poste !== this._selectedIntitulePoste()) {
        this.onIntitulePosteChange(poste);
      }
    });

    this.form.get('numPoste')?.valueChanges.subscribe(poste => {
      if (poste !== this._selectedNumPoste()) {
        this.onNumPosteChange(poste);
      }
    });

    this.form.get('typeRecrutement')?.valueChanges.subscribe(type => {
      if (type !== this._selectedTypeRecrutement()) {
        this._selectedTypeRecrutement.set(type);
      }
    });
  }

  private async loadDirectionsForPole(poleId: string): Promise<void> {
    try {
      this.dataService.clearDirections();
      this.dataService.clearServices();
      this.dataService.clearPostes();
      
      await this.dataService.loadDirections(poleId);
      this.handleDirectionAutoSelection();
      
      this.form.updateValueAndValidity();
      this.onValidationChange();
    } catch (error) {
      console.error('Failed to load directions:', error);
    }
  }

  private async loadServicesForDirection(directionId: string): Promise<void> {
    try {
      this.dataService.clearServices();
      this.dataService.clearPostes();
      
      await this.dataService.loadServices(directionId);
      this.handleDemandeServiceAutoSelection();
      
      const service = this._selectedService();
      await this.loadPostesForDirectionAndService(directionId, service?.id);
      
      this.form.updateValueAndValidity();
      this.onValidationChange();
    } catch (error) {
      console.error('Failed to load services:', error);
    }
  }

  private async loadPostesForDirectionAndService(directionId: string, serviceId?: string): Promise<void> {
    try {
      this.dataService.clearPostes();
      await this.dataService.loadPostes(directionId, serviceId);
      
      this.form.updateValueAndValidity();
      this.onValidationChange();
    } catch (error) {
      console.error('Failed to load postes:', error);
    }
  }

  private handleDirectionAutoSelection(): void {
    const autoDirection = this.autoSelectedDirection();
    if (autoDirection && this._selectedDirection() !== autoDirection) {
      this._selectedDirection.set(autoDirection);
      this.form.patchValue({ direction: autoDirection }, { emitEvent: false });
      
      if (autoDirection.id) {
        this.loadServicesForDirection(autoDirection.id);
      }
    }
  }

  private handleDemandeServiceAutoSelection(): void {
    if (this.shouldAutoSetDemandeService()) {
      const demande = this._demandeService();
      if (demande && this._selectedService() !== demande) {
        this._selectedService.set(demande);
        this.form.patchValue({ service: demande }, { emitEvent: false });
      }
    }
  }

  onPoleChange(pole: Pole | null): void {
    if (pole === this._selectedPole()) return;
    
    this._selectedPole.set(pole);
    this._selectedDirection.set(null);
    this._selectedService.set(null);
    this.clearPosteSelection();
    
    this.clearFieldErrors(['direction', 'service', 'intitulePoste', 'numPoste']);
    
    if (pole?.id) {
      this.loadDirectionsForPole(pole.id);
    }
  }

  onDirectionChange(direction: Direction | null): void {
    if (direction === this._selectedDirection()) return;
    
    this._selectedDirection.set(direction);
    this._selectedService.set(null);
    this.clearPosteSelection();
    
    // Immediately clear posts list to prevent invalid selections
    this.dataService.clearPostes();
    
    this.clearFieldErrors(['service', 'intitulePoste', 'numPoste']);
    
    if (direction?.id) {
      this.loadServicesForDirection(direction.id);
    }
  }

  onServiceChange(service: ServiceFonctionnel | null): void {
    if (service === this._selectedService()) return;
    
    this._selectedService.set(service);
    this.clearPosteSelection();
    
    // Immediately clear posts list to prevent invalid selections
    this.dataService.clearPostes();
    
    this.clearFieldErrors(['intitulePoste', 'numPoste']);
    
    const direction = this._selectedDirection();
    if (direction?.id) {
      this.loadPostesForDirectionAndService(direction.id, service?.id);
    }
  }

  onIntitulePosteChange(poste: Poste | null): void {
    this._selectedIntitulePoste.set(poste);
    if (poste !== this._selectedNumPoste()) {
      this._selectedNumPoste.set(poste);
      this.form.patchValue({ numPoste: poste }, { emitEvent: false });
    }
    this.posteSelected.emit(poste);
  }

  onNumPosteChange(poste: Poste | null): void {
    this._selectedNumPoste.set(poste);
    if (poste !== this._selectedIntitulePoste()) {
      this._selectedIntitulePoste.set(poste);
      this.form.patchValue({ intitulePoste: poste }, { emitEvent: false });
    }
    this.posteSelected.emit(poste);
  }

  private clearPosteSelection(): void {
    this._selectedIntitulePoste.set(null);
    this._selectedNumPoste.set(null);
    this.form.patchValue({ intitulePoste: null, numPoste: null }, { emitEvent: false });
    this.posteSelected.emit(null);
  }

  private clearFieldErrors(fieldNames: string[]): void {
    fieldNames.forEach(fieldName => {
      this.form.get(fieldName)?.setErrors(null);
    });
  }

  getPosteDisplayValue = (poste: Poste | null): string => {
    return poste ? `${poste.libelle} ${poste.code || ''}` : '';
  };

  getNumeroPosteDisplayValue = (poste: Poste | null): string => {
    return poste ? poste.numero?.toString() || '' : '';
  };
  
  getTypeRecrutementDisplayValue = (item: TypeRecrutement | null): string => {
    return item ? item.libelle : '';
  };

  getPoleDisplayValue = (item: Pole | null): string => {
    return item ? item.libelle : '';
  };

  getDirectionDisplayValue = (item: Direction | null): string => {
    return item ? item.libelle : '';
  };

  getServiceDisplayValue = (item: ServiceFonctionnel | null): string => {
    return item ? item.libelle : '';
  };

  getMotifLiberationDisplayValue = (item: any | null): string => {
    return item ? item.libelle : '';
  };
  
  filterNumeroPoste = (poste: Poste, searchText: string): boolean => {
    return poste?.numero?.toString().toLowerCase().includes(searchText.toLowerCase()) || false;
  };

  writeValue(value: InformationsFormValue | DemandeRecrutementInformationsValue): void {
    if (!value) return;
    
    const mappedValue = this.mapToInformationsFormValue(value);
    this._externalValue.set(mappedValue);
    this.pendingPrefill = mappedValue;
    this._selectedTypeRecrutement.set(mappedValue.typeRecrutement ?? null);
    this._selectedPole.set(mappedValue.pole ?? null);
    this._selectedDirection.set(mappedValue.direction ?? null);
    this._selectedService.set(mappedValue.service ?? null);
    this._selectedIntitulePoste.set(mappedValue.intitulePoste ?? null);
    this._selectedNumPoste.set(mappedValue.numPoste ?? null);
    const nextDemandeService = mappedValue.service ?? this.demandeService ?? null;
    this._demandeService.set(nextDemandeService);
    
    this.form.patchValue(mappedValue, { emitEvent: true });
    this.form.updateValueAndValidity();
    this.onValidationChange();
    if (this.initialDataLoaded) {
      void this.ensureCascadingDataForPrefilledValue(mappedValue);
    }
  }

  registerOnChange(fn: (value: InformationsFormValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidationChange = fn;
  }

  validate(): ValidationErrors | null {
    return this.form.valid ? null : { invalidInformationsData: { invalidControls: this.getInvalidControls() } };
  }

  setDisabledState(isDisabled: boolean): void {
    this._readOnly.set(isDisabled);
    if (isDisabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
      this.form.controls['pole'].disable({ emitEvent: false });
    }
  }

  public getInvalidControls(): string[] {
    const invalidControls: string[] = [];
    const controls = this.form.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalidControls.push(`InformationsForm: ${name}`);
      }
    }
    return invalidControls;
  }

  private mapToInformationsFormValue(value: any): InformationsFormValue {
    return {
      typeRecrutement: value.typeRecrutement || null,
      pole: value.pole || null,
      direction: value.direction || null,
      service: value.service || null,
      numPoste: value.numPoste || null,
      intitulePoste: value.intitulePoste || null,
      motifLiberation: value.motifLiberation || null
    };
  }

  private async ensureCascadingDataForPrefilledValue(value: InformationsFormValue): Promise<void> {
    if (!this.initialDataLoaded) {
      this.pendingPrefill = value;
      return;
    }

    const poleId = value.pole?.id;
    const directionId = value.direction?.id;
    const serviceId = value.service?.id ?? undefined;

    try {
      if (poleId) {
        const directions = this.dataService.directionList();
        const shouldLoadDirections =
          !directions.length || (directionId ? !directions.some(direction => direction.id === directionId) : false);

        if (shouldLoadDirections) {
          await this.loadDirectionsForPole(poleId);
        }
      }

      if (directionId) {
        const services = this.dataService.serviceFonctionnelList();
        const shouldLoadServices =
          !services.length || (serviceId ? !services.some(service => service.id === serviceId) : true);

        if (shouldLoadServices) {
          await this.loadServicesForDirection(directionId);
        } else {
          const postes = this.dataService.posteList();
          const posteId = value.intitulePoste?.id ?? value.numPoste?.id ?? null;
          const shouldLoadPostes =
            !postes.length || (posteId ? !postes.some(poste => poste.id === posteId) : true);

          if (shouldLoadPostes) {
            await this.loadPostesForDirectionAndService(directionId, serviceId);
          }
        }
      }
    } catch (error) {
      console.error('Failed to synchronize dependant recruitment lists:', error);
    }
  }
}
